import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { api_editBooking, api_createCateringOrder } from '../../../api/catering/booking';
import { api_getVendors } from '../../../api/vendor';
import { toast } from 'react-hot-toast';
import { myToastWarning } from '../../../helpers/myToast';

type Vendor = {
	_id: string;
	name: string;
	location: string;
	phoneNumber: string;
	type: string;
	createdAt: string;
	updatedAt: string;
	__v: number;
};

const EditBooking = () => {
	const booking = useLocation().state;
	const [loading, setLoading] = useState<boolean>(false);
	const [decorVendor, setDecorVendor] = useState<Vendor[]>([]);
	const [photoVendor, setPhotoVendor] = useState<Vendor[]>([]);
	const [videoVendor, setVideoVendor] = useState<Vendor[]>([]);
	const [cakeVendor, setCakeVendor] = useState<Vendor[]>([]);
	const [partyAccessoriesVendor, setPartyAccessoriesVendor] = useState<Vendor[]>([]);
	const [decorId, setDecorId] = useState<string>('');
	const [photoId, setPhotoId] = useState<string>('');
	const [videoId, setVideoId] = useState<string>('');
	const [cakeId, setCakeId] = useState<string>('');
	const [partyAccessoriesId, setPartyAccessoriesId] = useState<string>('');
	const [status, setStatus] = useState<string>('pending');
	const [canActivate, setCanActivate] = useState<boolean>(false);
	const [guranteePrice, setGuranteePrice] = useState<number | string>('');

	const [decorPrice, setDecorPrice] = useState<number>(0);
	const [photoPrice, setPhotoPrice] = useState<number>(0);
	const [videoPrice, setVideoPrice] = useState<number>(0);
	const [cakePrice, setCakePrice] = useState<number>(0);
	const [partyAccessoriesPrice, setPartyAccessoriesPrice] = useState<number>(0);
	const [foodCateringPrice, setFoodCateringPrice] = useState<number>(0);
	const [partyCateringPrice, setPartyCateringPrice] = useState<number>(0);

	const navigate = useNavigate();

	useEffect(() => {
		if (booking) {
			setDecorId(booking?.decor?.vendor?._id || '');
			setPhotoId(booking?.photo?.vendor?._id || '');
			setVideoId(booking?.video?.vendor?._id || '');
			setCakeId(booking?.cake?.vendor?._id || '');
			setPartyAccessoriesId(booking?.partyAccessories?.vendor?._id || '');
			setStatus(booking?.status || 'pending');

			setDecorPrice(booking?.decor?.price || 0);
			setPhotoPrice(booking?.photo?.price || 0);
			setVideoPrice(booking?.video?.price || 0);
			setCakePrice(booking?.cake?.price || 0);
			setPartyAccessoriesPrice(booking?.partyAccessories?.price || 0);

			setFoodCateringPrice(booking?.foodCatering?.price || 0);
			setPartyCateringPrice(booking?.partyCatering?.totalPrice || 0);
		}
	}, [booking]);

	useEffect(() => {
		fetchVendors();
	}, []);

	useEffect(() => {
		setCanActivate(!!(decorId && photoId && videoId && cakeId && partyAccessoriesId));
	}, [decorId, photoId, videoId, cakeId, partyAccessoriesId]);

	const fetchVendors = async () => {
		try {
			const res = await api_getVendors();
			if (res.isSuccess) {
				const vendors = res.data;
				setDecorVendor(vendors.filter((vendor: Vendor) => vendor.type === 'decor'));
				setPhotoVendor(vendors.filter((vendor: Vendor) => vendor.type === 'photo'));
				setVideoVendor(vendors.filter((vendor: Vendor) => vendor.type === 'video'));
				setCakeVendor(vendors.filter((vendor: Vendor) => vendor.type === 'cake'));
				setPartyAccessoriesVendor(vendors.filter((vendor: Vendor) => vendor.type === 'partyAccessories'));
			} else {
				toast.error('Failed to fetch vendors');
			}
		} catch (error) {
			console.error('Error fetching vendors:', error);
			toast.error('Failed to fetch vendors');
		}
	};

	const editBooking = async () => {
		if (!decorId || !photoId || !videoId || !cakeId || !partyAccessoriesId) {
			myToastWarning('Please select at least one vendor');
			return;
		}
		try {
			setLoading(true);
			const payload = {
				decor: { vendor: decorId, price: decorPrice },
				photo: { vendor: photoId, price: photoPrice },
				video: { vendor: videoId, price: videoPrice },
				cake: { vendor: cakeId, price: cakePrice },
				partyAccessories: { vendor: partyAccessoriesId, price: partyAccessoriesPrice },
				foodCatering: { price: foodCateringPrice },
				partyCatering: { totalPrice: partyCateringPrice },
				status: status,
			};
	

			const res = await api_editBooking(payload, booking._id);
			const message = typeof res.message === 'string' ? res.message : 'Success';
			if (res.isSuccess) {
				toast.success(message);
				if (status === 'active') {
					const orderPrice = decorPrice + partyAccessoriesPrice + cakePrice + photoPrice + videoPrice + foodCateringPrice + partyCateringPrice;
					const orderPayload = {
						cateringBooking: booking._id,
						orderPrice: orderPrice,
						guranteePrice: guranteePrice,
						totalPrice: orderPrice + Number(guranteePrice),
					};
					const orderRes = await api_createCateringOrder(orderPayload);
					const orderMessage = typeof orderRes.message === 'string' ? orderRes.message : 'Order created successfully';
					if (orderRes.isSuccess) {
						toast.success(orderMessage);
					} else {
						toast.error(orderMessage || 'Failed to create order');
					}
				}

				setTimeout(() => {
					navigate(-1);
				}, 100);
			} else {
				toast.error(message || 'Failed to update booking');
			}
		} catch (error) {
			console.error('Error editing booking:', error);
			toast.error('Failed to update booking');
		} finally {
			setLoading(false);
		}
	};

	const handleToggleStatus = () => {
		if (canActivate) {
			setStatus(status === 'active' ? 'pending' : 'active');
		}
	};

	return (
		<div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', flex: 1, padding: '40px' }}>
			<div className='mb-3'>
				<h3>Edit Booking</h3>
			</div>

			<div className='mb-3'>
				<label htmlFor='decorVendor' className='form-label'>
					Decor Vendor
				</label>
				<select
					id='decorVendor'
					value={decorId}
					onChange={(e) => setDecorId(e.target.value)}
					className='form-control'
					style={{ width: '400px' }}
				>
					<option value=''>Select a vendor</option>
					{decorVendor.map((vendor) => (
						<option key={vendor._id} value={vendor._id}>
							{vendor.name}
						</option>
					))}
				</select>
				<input
					type='number'
					value={decorPrice}
					onChange={(e) => setDecorPrice(Number(e.target.value))}
					className='form-control mt-2'
					placeholder='Enter decor price'
				/>
			</div>

			<div className='mb-3'>
				<label htmlFor='photoVendor' className='form-label'>
					Photo Vendor
				</label>
				<select
					id='photoVendor'
					value={photoId}
					onChange={(e) => setPhotoId(e.target.value)}
					className='form-control'
					style={{ width: '400px' }}
				>
					<option value=''>Select a vendor</option>
					{photoVendor.map((vendor) => (
						<option key={vendor._id} value={vendor._id}>
							{vendor.name}
						</option>
					))}
				</select>
				<input
					type='number'
					value={photoPrice}
					onChange={(e) => setPhotoPrice(Number(e.target.value))}
					className='form-control mt-2'
					placeholder='Enter photo price'
				/>
			</div>

			<div className='mb-3'>
				<label htmlFor='videoVendor' className='form-label'>
					Video Vendor
				</label>
				<select
					id='videoVendor'
					value={videoId}
					onChange={(e) => setVideoId(e.target.value)}
					className='form-control'
					style={{ width: '400px' }}
				>
					<option value=''>Select a vendor</option>
					{videoVendor.map((vendor) => (
						<option key={vendor._id} value={vendor._id}>
							{vendor.name}
						</option>
					))}
				</select>
				<input
					type='number'
					value={videoPrice}
					onChange={(e) => setVideoPrice(Number(e.target.value))}
					className='form-control mt-2'
					placeholder='Enter video price'
				/>
			</div>

			<div className='mb-3'>
				<label htmlFor='cakeVendor' className='form-label'>
					Cake Vendor
				</label>
				<select
					id='cakeVendor'
					value={cakeId}
					onChange={(e) => setCakeId(e.target.value)}
					className='form-control'
					style={{ width: '400px' }}
				>
					<option value=''>Select a vendor</option>
					{cakeVendor.map((vendor) => (
						<option key={vendor._id} value={vendor._id}>
							{vendor.name}
						</option>
					))}
				</select>
				<input
					type='number'
					value={cakePrice}
					onChange={(e) => setCakePrice(Number(e.target.value))}
					className='form-control mt-2'
					placeholder='Enter cake price'
				/>
			</div>

			<div className='mb-3'>
				<label htmlFor='partyAccessoriesVendor' className='form-label'>
					Party Accessories Vendor
				</label>
				<select
					id='partyAccessoriesVendor'
					value={partyAccessoriesId}
					onChange={(e) => setPartyAccessoriesId(e.target.value)}
					className='form-control'
					style={{ width: '400px' }}
				>
					<option value=''>Select a vendor</option>
					{partyAccessoriesVendor.map((vendor) => (
						<option key={vendor._id} value={vendor._id}>
							{vendor.name}
						</option>
					))}
				</select>
				<input
					type='number'
					value={partyAccessoriesPrice}
					onChange={(e) => setPartyAccessoriesPrice(Number(e.target.value))}
					className='form-control mt-2'
					placeholder='Enter accessories price'
				/>
			</div>
			<div className='mb-3'>
				<label htmlFor='foodCateringPrice' className='form-label'>Food Catering Price</label>
				<input
					id='foodCateringPrice'
					type='number'
					value={foodCateringPrice}
					onChange={(e) => setFoodCateringPrice(Number(e.target.value))}
					className='form-control'
					style={{ width: '400px' }}
					placeholder='Enter food catering price'
				/>
			</div>
			<div className='mb-3'>
				<label htmlFor='partyCateringPrice' className='form-label'>Party Catering Price</label>
				<input
					id='partyCateringPrice'
					type='number'
					value={partyCateringPrice}
					onChange={(e) => setPartyCateringPrice(Number(e.target.value))}
					className='form-control'
					style={{ width: '400px' }}
					placeholder='Enter party catering price'
				/>
			</div>

			<div className='mb-3'>
				<label htmlFor='guranteePrice' className='form-label'>Gurantee Price</label>
				<input
					id='guranteePrice'
					type='number'
					value={guranteePrice}
					onChange={(e) => setGuranteePrice(e.target.value)}
					className='form-control'
					style={{ width: '400px' }}
					placeholder='Enter gurantee price'
				/>
			</div>
			<div className='mb-3'>
				<button
					type='button'
					className={`btn btn-lg ${status === 'active' ? 'btn-success' : 'btn-secondary'}`}
					style={{ width: '400px' }}
					onClick={handleToggleStatus}
					disabled={!canActivate}
				>
					{status === 'active' ? 'Deactivate' : 'Activate'}
				</button>
			</div>

			<button onClick={editBooking} type='button' className='btn btn-lg btn-primary' disabled={loading}>
				{loading ? 'Loading..' : 'Submit'}
			</button>
		</div>
	);
};

export default EditBooking;
